import {
  AbsoluteCenter,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  Heading,
  IconButton,
  Input,
  Link,
  SimpleGrid,
  Spinner,
  Text,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon, QuestionIcon } from '@chakra-ui/icons';
import {canDeleteDealApi, createDealApi, getDealApi, updateDealApi} from 'api/dealApi';
import {CurrencyNumberInput, PercentageNumberInput, TwNumberInput} from 'components/NumberInput';
import {TwDatePicker} from 'components/DatePicker';
import Card from 'components/card/Card';
import IrrCapPeriodInput from 'components/inputs/IrrCapPeriodInput';
import SelectInput from 'components/inputs/SelectInput';
import BackLink from 'components/links/BackLink';
import {
  DEAL_TYPE,
  DEAL_TYPE_META,
  FEE_TYPE,
  FUNDING_TYPE,
  FUNDING_TYPE_META,
  REPAYMENT_FREQUENCY_TYPE_META,
  TOTAL_TYPE_META,
  isIrrCapFeeType,
} from 'constants/dealConstants';
import {getFileContent, renderInputElement} from 'helpers/formHelper';
import {generateSelectOptions} from 'helpers/inputHelper';
import {isObjectEmpty} from 'helpers/objectHelper';
import {useFeeTypeOptions} from 'hooks/useFeeTypeOptions';
import cloneDeep from 'lodash.clonedeep';
import { IRR_CAP_PERIOD } from 'utils/irrCapPeriod';
import React, {Fragment, useEffect, useMemo, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {useNavigate, useParams} from 'react-router-dom';
import {resolveAccelerationOnLoad, validateAcceleration} from './accelerationHelper';
import {useRepaymentSchedule} from './hooks/useRepaymentSchedule';
import {mapRoasThresholdsFromApi, mapRoasThresholdsToApi} from './roasCovenantMonth';
import { emptyRoasVectorRow, hasDuplicateRoasVectorMonths, mapRoasVectorFromApi, mapRoasVectorToApi } from './roasVector';
import RoasVectorEditor from './RoasVectorEditor';
import { useMarketingViews } from 'hooks/useMarketingViews';
import FileInput from "../../../components/inputs/FileInput";
import DownloadLink from "../../../components/links/DownloadLink";
import {HelperTooltip} from 'components/Tooltip';
import {createContractDocumentApi, getContractDocumentApi} from "../../../api/contractDocumentApi";

const FieldHelp = ({item}) => {
  if (item.helpLink) {
    return <HelperTooltip content={item.tooltip} helpLink={item.helpLink} />;
  }
  if (!item.tooltip) {
    return null;
  }
  return (
    <Tooltip label={item.tooltip} fontSize="xs" maxW="300px">
      <QuestionIcon boxSize={3} color="gray.500" cursor="help" />
    </Tooltip>
  );
};

const createFields = ({
  dealType,
  formValues,
  repaymentDaysOptions,
  daysBeforeEndOfMonthOptions,
  feeTypeOptions,
  isPageDisabled,
  contractFile,
  contractFileContent,
  onSelectFiles,
  marketingViewOptions,
  }) => {
  let fields = [
    {
      label: 'Deal Type *',
      name: 'dealType',
      element: SelectInput,
      placeholder: 'Select Deal Type',
      options: generateSelectOptions({ metaObj: DEAL_TYPE_META }),
      tooltip: 'Fixed: You receive a fixed repayment amount each month. ROAS: You receive a percentage of revenue earned by the merchant.',
      required: true,
    },
    {
      label: 'Deal Name *',
      name: 'name',
      element: Input,
      placeholder: 'Enter a deal name',
      tooltip: 'A descriptive name for this deal or merchant reference',
      required: true,
    },
    {
      label: 'Frequency *',
      name: 'repaymentFrequency',
      element: SelectInput,
      placeholder: 'Select payment frequency',
      options: generateSelectOptions({
        metaObj: REPAYMENT_FREQUENCY_TYPE_META,
      }),
      formatValue: (value) => {
        return REPAYMENT_FREQUENCY_TYPE_META[value]?.label;
      },
      tooltip: 'How often the customer has to make a repayments',
      required: true,
    },
    {
      label: 'Days *',
      name: 'repaymentDays',
      element: SelectInput,
      placeholder: 'Select repayment day',
      options: repaymentDaysOptions,
      isMulti: true,
      isDisabled: !formValues.repaymentFrequency,
      tooltip: 'When repayment is due - select specific day of month or end of month or on what weekday',
      required: true,
    },
    {
      label: 'Days Before End of Month',
      name: 'daysBeforeEndOfMonth',
      element: SelectInput,
      required: false,
      isNumber: true,
      options: daysBeforeEndOfMonthOptions,
      isHidden: !formValues?.repaymentDays?.includes(-1),
      tooltip: 'Number of days before month end to process the repayment',
    },
    {
      label: 'Number of Payments *',
      name: 'numberOfRepayments',
      element: TwNumberInput,
      placeholder: '0',
      tooltip: 'The maximum number of repayments the customer has per funding',
      required: true,
    },
    {
      label: 'Fee Type *',
      name: 'feeSettings.feeType',
      element: SelectInput,
      options: feeTypeOptions,
      placeholder: '-- Select Fee Type --',
      required: true,
      tooltip:
        'The method by which fees are calculated. For IRR Cap Daily, see Help → IRR Cap Daily.',
      helpLink: 'irr-cap-daily',
    },

    {
      label: 'Contract Document (PDF)',
      name: 'contractDocument',
      isCustomField: true,
      tooltip: 'Upload a PDF contract to auto-fill deal terms (optional)',
      required: false,
      element: (
          <Flex gap={1} direction={'column'}>
            <FileInput
                initialFile={contractFile}
                allowedFileTypes={['application/pdf']}
                onSelectFiles={onSelectFiles}
                disabled={isPageDisabled}
            />
            {contractFile && (<DownloadLink
                filename={contractFile.name}
                content={contractFileContent}
                mimeType="application/pdf"
            />)}
          </Flex>
      ),
    },

    {
      label: 'Fee *',
      name: 'feeSettings.feePct',
      element: PercentageNumberInput,
      required: true,
      isHidden: formValues.feeSettings?.feeType !== FEE_TYPE.FIXED,
      tooltip: 'The fixed percentage fee charged on each funding draw',
    },
    {
      label: 'IRR Cap *',
      name: 'feeSettings.irrCapPct',
      element: IrrCapPeriodInput,
      required: true,
      isHidden: !isIrrCapFeeType(formValues.feeSettings?.feeType),
      tooltip: 'Enter as monthly or annual. The typed rate and period are saved; monthly is converted to annual only when calculating fees and exposure.',
      props: {
        period: formValues.feeSettings?.irrCapPeriod || IRR_CAP_PERIOD.ANNUAL,
      },
    },
    {
      label: 'Effective Date',
      name: 'effectiveDate',
      element: TwDatePicker,
      tooltip: 'The effective start date of Twelve\'s funding commitment',
    },
    {
      label: 'Commitment Length',
      name: 'commitmentLength',
      element: TwNumberInput,
      placeholder: '0',
      tooltip: 'Commitment length in months. Commitment end date is calculated as Effective Date + Commitment Length',
    },
    {
      label: 'Funding Type *',
      name: 'fundingType',
      element: SelectInput,
      placeholder: 'Select Funding Type',
      options: [
        { label: FUNDING_TYPE_META[FUNDING_TYPE.PERCENTAGE_UA].label, value: FUNDING_TYPE.PERCENTAGE_UA },
        { label: FUNDING_TYPE_META[FUNDING_TYPE.FIXED_AMOUNT].label, value: FUNDING_TYPE.FIXED_AMOUNT },
      ],
      tooltip: 'Choose how to calculate funding: as a percentage of UA or fixed amount',
      required: true,
    },
    {
      label: 'Funding Percentage *',
      name: 'fundingPercentage',
      element: PercentageNumberInput,
      placeholder: '0',
      tooltip: 'The percentage of UA for each funding draw',
      isHidden: formValues.fundingType !== FUNDING_TYPE.PERCENTAGE_UA,
      required: true,
    },
    {
      label: 'Marketing view',
      name: 'marketingViewId',
      element: SelectInput,
      placeholder: 'Default',
      options: marketingViewOptions,
      isHidden: !marketingViewOptions?.length,
      tooltip: 'Custom marketing view used to size funding: funding amount = view spend × funding %. Covenants always use all marketing. Repayment % uses all marketing unless the box below is checked.',
      helpLink: 'marketing-view-funding',
    },
    {
      label: 'Also use this view for repayment %',
      name: 'marketingViewForRepayment',
      element: Checkbox,
      isHidden: !marketingViewOptions?.length || !formValues.marketingViewId,
      tooltip: 'Checked: repayment % = purchase price ÷ view spend, so you collect a higher share of revenue. Unchecked: repayment % = purchase price ÷ all marketing spend.',
      helpLink: 'marketing-view-funding',
    },
    {
      label: 'Funding Amount *',
      name: 'fundingAmount',
      element: CurrencyNumberInput,
      placeholder: '0',
      tooltip: 'The fixed funding amount for each draw',
      isHidden: formValues.fundingType !== FUNDING_TYPE.FIXED_AMOUNT,
      required: true,
    },
    {
      label: 'Max Funding Amount for Cohort *',
      name: 'maxFundingAmount',
      element: CurrencyNumberInput,
      placeholder: 'e.g., 20000000',
      tooltip: 'The maximum amount that can be funded per cohort, e.g., $2 million',
      isHidden: formValues.fundingType === FUNDING_TYPE.FIXED_AMOUNT,
      required: true,
    },
    {
      label: 'Max Exposure',
      name: 'maxExposure',
      element: CurrencyNumberInput,
      placeholder: 'e.g., 5000000',
      tooltip: 'The maximum total exposure including the current funding at any given time',
      required: false,
    },
    {
      label: 'Max MOM Growth',
      name: 'maxMomGrowth',
      element: PercentageNumberInput,
      tooltip: 'The maximum % by which funding can grow from one month to the next (vs the previous month\'s funding amount)',
      required: false,
    },
    {
      label: 'Max MOM Funding Amount',
      name: 'maxMomFundingAmount',
      element: CurrencyNumberInput,
      placeholder: 'e.g., 5000000',
      tooltip: 'The maximum amount funding can increase versus the previous funding',
      required: false,
    },
    {
      label: 'Max CAC',
      name: 'maxCac',
      element: CurrencyNumberInput,
      placeholder: 'e.g., 250',
      tooltip: 'The maximum customer acquisition cost (CAC) allowed',
      required: false,
    },
    {
      label: 'MOM Delta (%)',
      name: 'momDeltaPct',
      element: PercentageNumberInput,
      placeholder: '0',
      tooltip: 'Minimum required month-over-month ROAS growth for a funded cohort (inherited by installments from this deal). Each month, the cohort\'s cumulative ROAS must increase by at least this many percentage points versus the previous month (the first month is measured against 0). If a month\'s growth falls short, a "Min ROAS Growth" alert is raised.',
      required: false,
    },
    {
      label: 'MOM Delta Until ROAS (%)',
      name: 'momDeltaUntilRoasPct',
      element: PercentageNumberInput,
      placeholder: 'e.g., 100',
      tooltip: 'Stop checking the MOM Delta growth constraint once cumulative ROAS reaches this level (e.g. 100%). Leave empty to keep checking indefinitely.',
      required: false,
    },
    {
      label: 'Second Level Breach (%)',
      name: 'secondLevelBreachPct',
      element: PercentageNumberInput,
      placeholder: 'e.g., 80',
      tooltip: 'This % means if the actual MOM delta is at or below this fraction of the MOM Delta value, or if the actual ROAS is at or below this fraction of the ROAS threshold, then we will collect from all cohorts\' revenue. Example: MOM Delta is set to 10%, Second Level Breach is set to 80%, so if the actual MOM delta is 8% or less this is a second level breach.',
      required: false,
    },
    {
      label: '# of past cohorts to check',
      name: 'pastCohortsToCheck',
      element: TwNumberInput,
      placeholder: '0',
      tooltip: 'The minimum number of past cohorts we have to check that they do not breach the ROAS covenant',
      required: false,
    },
    {
      label: '# of cohort breaches for full Repayment from all cohorts',
      name: 'cohortBreachesForFullRepayment',
      element: TwNumberInput,
      placeholder: 'e.g., 3',
      tooltip: 'If this many cohorts are breached, then we collect revenue from all Tested Cohorts.',
      required: false,
    },
    {
      label: (
        <>
          Min.{' '}
          <Link href="/help#run-rate-cashflow-model" isExternal color="blue.500" textDecoration="underline">
            Runrate
          </Link>
        </>
      ),
      name: 'minRunrate',
      element: TwNumberInput,
      placeholder: '0',
      tooltip: 'The minimal runrate the customer needs to get a loan',
      required: false,
    },
    {
      label: 'Min. Cash Balance',
      name: 'minCashBalance',
      element: CurrencyNumberInput,
      placeholder: '0',
      tooltip: 'The minimum cash available in the bank accounts the customer needs to get a loan',
      required: false,
    },
  ];

  if (dealType === DEAL_TYPE.ROAS) {
    fields = fields.concat(
        {
          label: 'Revenue Margin *',
          name: 'pctFromRevenueShare',
          element: PercentageNumberInput,
          required: true,
          tooltip:
            'Factor applied to the installment\'s pro-rata share of revenue. Example: if you fund 80%, repayments use 80% of the selected revenue, then multiplied by this margin (100% = take that pro-rata share as-is; higher/lower adjusts it).',
        },
        {
          label: 'Revenue Type *',
          name: 'revenueTotalType',
          element: SelectInput,
          required: true,
          placeholder: 'Select revenue type',
          options: generateSelectOptions({ metaObj: TOTAL_TYPE_META }),
          tooltip: 'The type of revenue metric used',
        },
        {
          label: 'True Up',
          name: 'isTrueUp',
          element: Checkbox,
          required: false,
          isDisabled:
            formValues.feeSettings?.feeType === FEE_TYPE.IRR_CAP ||
            formValues.feeSettings?.feeType === FEE_TYPE.IRR_CAP_DAILY,
          tooltip:
            formValues.feeSettings?.feeType === FEE_TYPE.IRR_CAP ||
            formValues.feeSettings?.feeType === FEE_TYPE.IRR_CAP_DAILY
              ? 'Required when Fee Type is IRR Cap'
              : 'Enable adjustment mechanism for minimum based on performance',
        },
        {
          label: 'Enable Acceleration',
          name: 'accelerationEnabled',
          element: Checkbox,
          required: false,
          tooltip:
            'When enabled, you can set ROAS thresholds. When the cohort meets those thresholds, acceleration kicks in and repayments use a percentage of the selected Gross/Net revenue instead of the standard revenue margin.',
        },
        {
          label: 'Acceleration %',
          name: 'accelerationPct',
          element: TwNumberInput,
          placeholder: 'e.g. 100',
          required: formValues.accelerationEnabled === true,
          isHidden: formValues.accelerationEnabled !== true,
          tooltip: 'The percentage of the selected revenue type applied when acceleration is activated (e.g. 100 = 100%)',
        },
        {
          label: 'Acceleration Revenue Type',
          name: 'accelerationRevenueTotalType',
          element: SelectInput,
          required: formValues.accelerationEnabled === true,
          isHidden: formValues.accelerationEnabled !== true,
          placeholder: 'Select revenue type',
          options: generateSelectOptions({ metaObj: TOTAL_TYPE_META }),
          tooltip: 'The Gross/Net revenue used when acceleration is activated',
        },
        {
          label: 'ROAS Covenant Revenue Type',
          name: 'roasCovenantRevenueTotalType',
          element: SelectInput,
          required: false,
          placeholder: 'Gross (default)',
          options: generateSelectOptions({ metaObj: TOTAL_TYPE_META }),
          tooltip: 'Gross or net revenue used to measure ROAS for threshold and MOM delta covenants. Leave empty for gross.',
        },
        {
          label: 'ROAS Covenant Multiplier (%)',
          name: 'roasCovenantMultiplier',
          element: PercentageNumberInput,
          placeholder: '100',
          required: false,
          tooltip:
            'Percent of covenant revenue counted toward ROAS (e.g. 60% with $200k gross counts as $120k). Leave empty for 100%. Compared as (revenue × % ÷ 100) ÷ marketing spend.',
        }
    );
  }
  return fields;
};

const Deal = () => {
  const [loading, setLoading] = useState(false);
  const { handleSubmit, reset, control, watch, setValue} = useForm();
  const [contractFile, setContractFile] = useState(null);
  const [contractFileContent, setContractFileContent] = useState(null);
  const [roasThresholds, setRoasThresholds] = useState([{ id: 1, month: '', minRoasPct: '' }]);
  const [roasVector, setRoasVector] = useState([emptyRoasVectorRow()]);
  const [documentsToCheck, setDocumentsToCheck] = useState([{ id: 1, name: '' }]);
  const [hasDuplicateMonths, setHasDuplicateMonths] = useState(false);
  // Existing deals are editable only when they have no active (non-deleted) installments.
  const [isDealEditable, setIsDealEditable] = useState(true);

  const toast = useToast();
  const navigate = useNavigate();
  const formValues = watch();
  const {repaymentFrequency, dealType} = formValues;

  const {
    repaymentDaysOptions,
    daysBeforeEndOfMonthOptions,
    onRepaymentFrequencyFieldChange,
  } = useRepaymentSchedule({repaymentFrequency});

  const {feeTypeOptions} = useFeeTypeOptions({dealType});

  const {customerId, dealId} = useParams();
  const { options: marketingViewOptions } = useMarketingViews(customerId);

  const isPageDisabled = !!dealId && !isDealEditable;

  // Check for duplicate months in ROAS thresholds
  const checkDuplicateMonths = (thresholds) => {
    const months = thresholds
      .map(t => t.month)
      .filter(m => m !== '' && m !== null && m !== undefined);
    const uniqueMonths = new Set(months);
    return months.length !== uniqueMonths.size;
  };

  useEffect(() => {
    const init = async () => {
      if (dealId) {
        const [deal, canDeleteResult] = await Promise.all([
          getDeal({customerId, dealId}),
          canDeleteDealApi({customerId, dealId}).catch(() => ({ canDelete: false })),
        ]);
        setIsDealEditable(!!canDeleteResult?.canDelete);

        const file = await getContractDocumentApi({
          customerId, dealOrInstallmentId: dealId, fileName: deal.contractFile});

        if (file) {
          setContractFile(file);
          setContractFileContent(file);
        }

        // Load ROAS thresholds from deal data (API 1-based → UI 0-based MOB)
        if (deal.roasThresholds && deal.roasThresholds.length > 0) {
          const loadedThresholds = mapRoasThresholdsFromApi(deal.roasThresholds);
          setRoasThresholds(loadedThresholds);
          setHasDuplicateMonths(checkDuplicateMonths(loadedThresholds));
        }

        if (deal.roasVector && deal.roasVector.length > 0) {
          setRoasVector(mapRoasVectorFromApi(deal.roasVector));
        }

        if (deal.documentsToCheck && deal.documentsToCheck.length > 0) {
          setDocumentsToCheck(
            deal.documentsToCheck.map((name, index) => ({ id: index + 1, name: name || '' }))
          );
        }

        Object.assign(deal, resolveAccelerationOnLoad(deal));

        reset(deal);
      } else {
        setIsDealEditable(true);
        reset({
          daysBeforeEndOfMonth: 0,
        });
      }
    };

    init();
  }, []);

  useEffect(() => {
    if (!dealType) {
      return;
    }

    const values = cloneDeep(formValues);
    if (dealType === DEAL_TYPE.ROAS) {
      if (values.isTrueUp === undefined) {
        values.isTrueUp = false;
      }
      // Fixed fee is always valid; leave fee type as-is when switching to ROAS.
    } else if (dealType === DEAL_TYPE.FIXED) {
      delete values.pctFromRevenueShare;
      delete values.revenueTotalType;
      delete values.isTrueUp;
      delete values.accelerationEnabled;
      delete values.accelerationPct;
      delete values.accelerationRevenueTotalType;
      delete values.roasCovenantRevenueTotalType;
      delete values.roasCovenantMultiplier;
      // Fixed deals only support Fixed fee type.
      if (!values.feeSettings) {
        values.feeSettings = {};
      }
      values.feeSettings.feeType = FEE_TYPE.FIXED;
    }

    reset(values);
  }, [dealType]);

  const onSelectFiles = async (files) => {
    let file = null
    if (files.length > 0) {
      file = files[0];
    } else {
      return;
    }
    setContractFile(file);
    getFileContent(file, setContractFileContent);
  };

  const fields = useMemo(() => {
    return createFields({
      dealType: formValues.dealType,
      formValues,
      repaymentDaysOptions,
      daysBeforeEndOfMonthOptions,
      feeTypeOptions,
      isPageDisabled,
      contractFile,
      contractFileContent,
      onSelectFiles,
      marketingViewOptions,
    });
  }, [
    formValues,
    repaymentDaysOptions,
    daysBeforeEndOfMonthOptions,
    feeTypeOptions,
    isPageDisabled,
    contractFile,
    contractFileContent,
    marketingViewOptions,
  ]);

  const getDeal = async () => {
    try {
      setLoading(true);
      const deal = await getDealApi({customerId, dealId});
      return deal;
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load deal',
        status: 'error',
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const saveDeal = async (data) => {
    try {
      setLoading(true);
      let savedDealId = dealId;
      if (dealId) {
        await updateDealApi({customerId, dealId, data});
      } else {
        savedDealId = await createDealApi({customerId, data});
      }

      if (contractFile) {
        await createContractDocumentApi({
          customerId: customerId,
          dealOrInstallmentId: savedDealId.toString(),
          file: contractFile,
        });
      }

      toast({
        title: 'Success',
        description: dealId ? 'Deal updated successfully' : 'Deal created successfully',
        status: 'success',
      });
      navigate(`/operations/${customerId}/deal`);
    } catch (error) {
      toast({
        title: 'Error',
        description: dealId ? 'Failed to update deal' : 'Failed to create deal',
        status: 'error',
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (data) => {
    if (data.repaymentNoMinimum === true) {
      data.isTrueUp = false;
    }
    if (
      data.feeSettings?.feeType === FEE_TYPE.IRR_CAP ||
      data.feeSettings?.feeType === FEE_TYPE.IRR_CAP_DAILY
    ) {
      data.isTrueUp = true;
    }
    const accelerationValidation = validateAcceleration(data);
    if (!accelerationValidation.isValid) {
      toast({
        title: 'Validation Error',
        description: accelerationValidation.message,
        status: 'error',
      });
      return;
    }
    if (!data.accelerationEnabled) {
      data.accelerationPct = null;
      data.accelerationRevenueTotalType = null;
    }
    delete data.accelerationEnabled;
    data.contractFile = contractFile?.name || null;
    if (data.effectiveDate === '' || data.effectiveDate == null) {
      data.effectiveDate = null;
    }
    if (data.commitmentLength === '' || data.commitmentLength == null) {
      data.commitmentLength = null;
    } else {
      data.commitmentLength = Number(data.commitmentLength);
    }
    // Commitment end date is derived server-side from effective date + commitment length.
    delete data.commitmentEndDate;
    // Include ROAS thresholds in the submitted data (UI 0-based MOB → API 1-based)
    data.roasThresholds = mapRoasThresholdsToApi(roasThresholds);
    data.roasVector = mapRoasVectorToApi(roasVector);
    if (!data.marketingViewId) {
      data.marketingViewId = null;
      data.marketingViewForRepayment = false;
    }
    if (data.roasCovenantMultiplier === '' || data.roasCovenantMultiplier == null) {
      data.roasCovenantMultiplier = null;
    } else {
      data.roasCovenantMultiplier = Number(data.roasCovenantMultiplier);
    }
    if (!data.roasCovenantRevenueTotalType) {
      data.roasCovenantRevenueTotalType = null;
    }
    data.documentsToCheck = documentsToCheck
      .map((doc) => (doc.name || '').trim())
      .filter((name) => name.length > 0);
    saveDeal(data);
  };

  useEffect(() => {
    if (
      formValues.feeSettings?.feeType === FEE_TYPE.IRR_CAP ||
      formValues.feeSettings?.feeType === FEE_TYPE.IRR_CAP_DAILY
    ) {
      setValue('isTrueUp', true);
    }
  }, [formValues.feeSettings?.feeType, setValue]);

  const onFieldChange = (name, value) => {
    onRepaymentFrequencyFieldChange({
      repaymentFrequency,
      setValue,
      name,
      value,
    });
    if (name === 'accelerationEnabled') {
      if (value === true) {
        if (formValues.accelerationPct == null) {
          setValue('accelerationPct', 100);
        }
        if (!formValues.accelerationRevenueTotalType) {
          setValue('accelerationRevenueTotalType', formValues.revenueTotalType);
        }
      } else {
        setValue('accelerationPct', null);
        setValue('accelerationRevenueTotalType', null);
      }
    }
    if (
      name === 'feeSettings.feeType' &&
      (value === FEE_TYPE.IRR_CAP || value === FEE_TYPE.IRR_CAP_DAILY)
    ) {
      setValue('isTrueUp', true);
      if (!formValues.feeSettings?.irrCapPeriod) {
        setValue('feeSettings.irrCapPeriod', IRR_CAP_PERIOD.ANNUAL);
      }
    }
    setValue(name, value);
  };

  function isFormFilled() {
    if (isObjectEmpty(formValues)) return false;

    // Check only required fields that are not hidden
    const requiredFields = fields.filter(f => f.required === true && !f.isHidden);

    for (const field of requiredFields) {
      const fieldName = field.name;
      // Handle nested field names like 'feeSettings.feeType'
      const value = fieldName.includes('.')
        ? fieldName.split('.').reduce((obj, key) => obj?.[key], formValues)
        : formValues[fieldName];

      // Check if value is empty (null, undefined, empty string, or empty array)
      if (value === null || value === undefined || value === '' ||
          (Array.isArray(value) && value.length === 0)) {
        return false;
      }
    }
    return true;
  }

  if (loading) {
    return (
        <AbsoluteCenter>
          <Spinner/>
        </AbsoluteCenter>
    );
  }

  return (
      <Box minW={1087} maxW={'max'} fontSize={14} mt={-10}>
        <BackLink
            text={'Back to deals table'}
            path={`/operations/${customerId}/deal`}
        />
        <Card mt={'100px'}>
          <Flex align="baseline" gap={10}>
            <Heading
                variant={'sectionHeader'}
                borderColor={'twelve.green.300'}
                mb={10}
            >
              Deal Setup
            </Heading>
          </Flex>

          <form onSubmit={handleSubmit(onSubmit)}>
            <FormControl>
              <SimpleGrid
                  w={'max'}
                  gridTemplateColumns={'min-content min-content'}
                  whiteSpace={'nowrap'}
                  justifyContent={'space-between'}
                  spacingY={4}
                  spacingX={10}
                  alignItems={'center'}
                  fontSize={'sm'}
              >
                {/* Upload Contract Document Section */}
                <Text gridColumn="1 / -1" fontWeight="bold" fontSize="md" mt={4} mb={2}>
                  Upload Contract Document
                </Text>
                {fields.filter(f => ['contractDocument'].includes(f.name)).map((item, index) => {
                      return item.isHidden ? null : (
                          <Fragment key={index}>
                            <Flex align="center" gap={1}>
                              <Text>{item.label}</Text>
                              <FieldHelp item={item} />
                            </Flex>
                            <Box w={200}>
                              {item.isCustomField ? (
                                  item.element
                              ) : (
                                  <Controller
                                      control={control}
                                      name={item.name}
                                      render={renderInputElement({
                                        item,
                                        styleProps: {fontWeight: 800, w: 200},
                                        showPrefix: true,
                                        isDisabled: isPageDisabled,
                                        onChange: (name, value) => {
                                          onFieldChange(name, value);
                                        },
                                      })}
                                  />)}
                            </Box>
                          </Fragment>
                      );
                    }
                )}

                {/* Basic Information Section */}
                <Text gridColumn="1 / -1" fontWeight="bold" fontSize="md" mt={6} mb={2}>
                  Basic Information
                </Text>
                {fields.filter(f => ['dealType', 'name'].includes(f.name)).map((item, index) => {
                      return item.isHidden ? null : (
                          <Fragment key={index}>
                            <Flex align="center" gap={1}>
                              <Text>{item.label}</Text>
                              <FieldHelp item={item} />
                            </Flex>
                            <Box w={200}>
                              {item.isCustomField ? (
                                  item.element
                              ) : (
                                  <Controller
                                      control={control}
                                      name={item.name}
                                      render={renderInputElement({
                                        item,
                                        styleProps: {fontWeight: 800, w: 200},
                                        showPrefix: true,
                                        isDisabled: isPageDisabled,
                                        onChange: (name, value) => {
                                          onFieldChange(name, value);
                                        },
                                      })}
                                  />)}
                            </Box>
                          </Fragment>
                      );
                    }
                )}

                {/* Funding Terms Section */}
                <Text gridColumn="1 / -1" fontWeight="bold" fontSize="md" mt={6} mb={2}>
                  Funding Terms
                </Text>
                {fields.filter(f => ['effectiveDate', 'commitmentLength'].includes(f.name)).map((item, index) => {
                      return item.isHidden ? null : (
                          <Fragment key={index}>
                            <Flex align="center" gap={1}>
                              <Text>{item.label}</Text>
                              <FieldHelp item={item} />
                            </Flex>
                            <Box w={200}>
                              {item.isCustomField ? (
                                  item.element
                              ) : (
                                  <Controller
                                      control={control}
                                      name={item.name}
                                      render={renderInputElement({
                                        item,
                                        styleProps: {fontWeight: 800, w: 200},
                                        showPrefix: true,
                                        isDisabled: isPageDisabled,
                                        onChange: (name, value) => {
                                          onFieldChange(name, value);
                                        },
                                      })}
                                  />)}
                            </Box>
                          </Fragment>
                      );
                    }
                )}

                {/* Funding Structure Subsection */}
                <Box gridColumn="1 / -1" borderLeft="3px solid" borderColor="blue.500" pl={4} mt={4}>
                  <Text fontWeight="semibold" fontSize="sm" color="blue.600" mb={3}>
                    Funding Structure
                  </Text>
                  <SimpleGrid
                    gridTemplateColumns={'min-content min-content'}
                    whiteSpace={'nowrap'}
                    spacingY={4}
                    spacingX={10}
                    alignItems={'center'}
                    fontSize={'sm'}
                    pb={4}
                  >
                    {fields.filter(f => ['fundingType', 'fundingPercentage', 'marketingViewId', 'marketingViewForRepayment', 'fundingAmount', 'maxFundingAmount', 'maxExposure', 'maxMomGrowth', 'maxMomFundingAmount', 'maxCac'].includes(f.name)).map((item, index) => {
                          return item.isHidden ? null : (
                              <Fragment key={index}>
                                <Flex align="center" gap={1}>
                                  <Text>{item.label}</Text>
                                  <FieldHelp item={item} />
                                </Flex>
                                <Box w={200}>
                                  {item.isCustomField ? (
                                      item.element
                                  ) : (
                                      <Controller
                                          control={control}
                                          name={item.name}
                                          render={renderInputElement({
                                            item,
                                            styleProps: {fontWeight: 800, w: 200},
                                            showPrefix: true,
                                            isDisabled: isPageDisabled,
                                            onChange: (name, value) => {
                                              onFieldChange(name, value);
                                            },
                                          })}
                                      />)}
                                </Box>
                              </Fragment>
                          );
                        }
                    )}
                  </SimpleGrid>
                </Box>

                {/* RepaymentSection */}
                <Text gridColumn="1 / -1" fontWeight="bold" fontSize="md" mt={6} mb={2}>
                  Repayment
                </Text>
                {fields.filter(f => [
                  'repaymentFrequency',
                  'repaymentDays',
                  'daysBeforeEndOfMonth',
                  'numberOfRepayments',
                ].includes(f.name)).map((item, index) => {
                      return item.isHidden ? null : (
                          <Fragment key={index}>
                            <Flex align="center" gap={1}>
                              <Text>{item.label}</Text>
                              <FieldHelp item={item} />
                            </Flex>
                            <Box w={200}>
                              {item.isCustomField ? (
                                  item.element
                              ) : (
                                  <Controller
                                      control={control}
                                      name={item.name}
                                      render={renderInputElement({
                                        item,
                                        styleProps: {fontWeight: 800, w: 200},
                                        showPrefix: true,
                                        isDisabled: isPageDisabled,
                                        onChange: (name, value) => {
                                          onFieldChange(name, value);
                                        },
                                      })}
                                  />)}
                            </Box>
                          </Fragment>
                      );
                    }
                )}

                {/* Financial Terms Section */}
                <Text gridColumn="1 / -1" fontWeight="bold" fontSize="md" mt={6} mb={2}>
                  Financial Terms
                </Text>
                {fields.filter(f => [
                  'feeSettings.feeType',
                  'feeSettings.feePct',
                  'feeSettings.irrCapPct',
                  'pctFromRevenueShare',
                  'revenueTotalType',
                  'isTrueUp',
                  'roasCovenantRevenueTotalType',
                  'roasCovenantMultiplier',
                ].includes(f.name)).map((item, index) => {
                      return item.isHidden ? null : (
                          <Fragment key={index}>
                            <Flex align="center" gap={1}>
                              <Text>{item.label}</Text>
                              <FieldHelp item={item} />
                            </Flex>
                            <Box
                              w={item.name === 'feeSettings.irrCapPct' ? 'auto' : 200}
                              minW={item.name === 'feeSettings.irrCapPct' ? '260px' : undefined}
                            >
                              {item.isCustomField ? (
                                  item.element
                              ) : (
                                  <Controller
                                      control={control}
                                      name={item.name}
                                      render={renderInputElement({
                                        item,
                                        styleProps: {
                                          fontWeight: 800,
                                          w: item.name === 'feeSettings.irrCapPct' ? undefined : 200,
                                        },
                                        showPrefix: true,
                                        isDisabled: isPageDisabled,
                                        onChange: (name, value) => {
                                          onFieldChange(name, value);
                                        },
                                      })}
                                  />)}
                            </Box>
                          </Fragment>
                      );
                    }
                )}

                {/* Acceleration Subsection */}
                {dealType === DEAL_TYPE.ROAS && (
                  <Box gridColumn="1 / -1" borderLeft="3px solid" borderColor="purple.500" pl={4} mt={4}>
                    <Text fontWeight="semibold" fontSize="sm" color="purple.600" mb={3}>
                      Acceleration
                    </Text>
                    <SimpleGrid
                      gridTemplateColumns={'min-content min-content'}
                      whiteSpace={'nowrap'}
                      spacingY={4}
                      spacingX={10}
                      alignItems={'center'}
                      fontSize={'sm'}
                      pb={4}
                    >
                      {fields.filter(f => ['accelerationEnabled', 'accelerationPct', 'accelerationRevenueTotalType'].includes(f.name)).map((item, index) => {
                            return item.isHidden ? null : (
                                <Fragment key={index}>
                                  <Flex align="center" gap={1}>
                                    <Text>{item.label}</Text>
                                    <FieldHelp item={item} />
                                  </Flex>
                                  <Box w={200}>
                                    {item.isCustomField ? (
                                        item.element
                                    ) : (
                                        <Controller
                                            control={control}
                                            name={item.name}
                                            render={renderInputElement({
                                              item,
                                              styleProps: {fontWeight: 800, w: 200},
                                              showPrefix: true,
                                              isDisabled: isPageDisabled,
                                              onChange: (name, value) => {
                                                onFieldChange(name, value);
                                              },
                                            })}
                                        />)}
                                  </Box>
                                </Fragment>
                            );
                          }
                      )}
                    </SimpleGrid>
                  </Box>
                )}

                {/* Condition Precedent - ROAS Thresholds Subsection */}
                {dealType === DEAL_TYPE.ROAS && (
                <Box gridColumn="1 / -1" borderLeft="3px solid" borderColor="orange.500" pl={4} mt={4}>
                  <Flex align="center" gap={1} mb={3}>
                    <Text fontWeight="semibold" fontSize="sm" color="orange.600">
                      Condition Precedent - ROAS Thresholds
                    </Text>
                    <Tooltip label="Minimum cumulative ROAS % required at each 0-based MOB (month 0 = first month) for the past 12 months to continue funding" fontSize="xs" maxW="300px">
                      <QuestionIcon boxSize={3} color="gray.500" cursor="help" />
                    </Tooltip>
                  </Flex>
                  <Box pb={4}>
                    {/* Column Headers */}
                    <Flex gap={4} mb={2} align="center">
                      <Box w={120}>
                        <Text fontSize="xs" fontWeight="semibold" color="gray.600">
                          Month
                        </Text>
                      </Box>
                      <Box w={150}>
                        <Text fontSize="xs" fontWeight="semibold" color="gray.600">
                          Min ROAS %
                        </Text>
                      </Box>
                      <Box w={10}>
                        <Text fontSize="xs" fontWeight="semibold" color="gray.600">
                          Action
                        </Text>
                      </Box>
                    </Flex>

                    {/* Data Rows */}
                    {[...roasThresholds].sort((a, b) => {
                      // Sort by month value, with empty values at the end
                      if (a.month === '' || a.month === null || a.month === undefined) return 1;
                      if (b.month === '' || b.month === null || b.month === undefined) return -1;
                      return Number(a.month) - Number(b.month);
                    }).map((threshold) => {
                      // Check if this month value is duplicated
                      const isDuplicate = roasThresholds.filter(
                        (t) => t.id !== threshold.id && t.month !== '' && t.month === threshold.month
                      ).length > 0;

                      return (
                        <Flex key={threshold.id} gap={4} mb={3} align="center">
                          <Box w={120}>
                            <TwNumberInput
                              value={threshold.month}
                              min={0}
                              onChange={(value) => {
                                const newThresholds = roasThresholds.map(t =>
                                  t.id === threshold.id ? { ...t, month: value } : t
                                );
                                setRoasThresholds(newThresholds);
                                setHasDuplicateMonths(checkDuplicateMonths(newThresholds));
                                setValue(`roasThreshold${threshold.id}Month`, value);
                              }}
                              onBlur={() => {
                                if (isDuplicate && threshold.month !== '') {
                                  toast({
                                    title: 'Duplicate Month',
                                    description: `Month ${threshold.month} is already used. Please choose a different month.`,
                                    status: 'error',
                                    duration: 4000,
                                    isClosable: true,
                                  });
                                }
                              }}
                              inputProps={{
                                fontWeight: 800,
                                borderColor: isDuplicate ? 'red.500' : undefined,
                                _focus: isDuplicate ? { borderColor: 'red.500' } : undefined
                              }}
                              isDisabled={isPageDisabled}
                            />
                            {isDuplicate && (
                              <Text fontSize="xs" color="red.500" mt={1} fontWeight="semibold">
                                Duplicate month - please fix before continuing
                              </Text>
                            )}
                          </Box>
                        <Box w={150}>
                          <PercentageNumberInput
                            value={threshold.minRoasPct}
                            onChange={(value) => {
                              const newThresholds = roasThresholds.map(t =>
                                t.id === threshold.id ? { ...t, minRoasPct: value } : t
                              );
                              setRoasThresholds(newThresholds);
                              setValue(`roasThreshold${threshold.id}MinRoasPct`, value);
                            }}
                            inputProps={{ fontWeight: 800 }}
                            isDisabled={isPageDisabled}
                          />
                        </Box>
                        {!isPageDisabled && (
                          <IconButton
                            icon={<DeleteIcon />}
                            size="sm"
                            colorScheme="red"
                            variant="ghost"
                            onClick={() => {
                              const newThresholds = roasThresholds.filter((t) => t.id !== threshold.id);
                              setRoasThresholds(newThresholds);
                              setHasDuplicateMonths(checkDuplicateMonths(newThresholds));
                            }}
                            isDisabled={roasThresholds.length === 1}
                            aria-label="Delete threshold"
                          />
                        )}
                      </Flex>
                    );
                  })}
                    {!isPageDisabled && (
                      <Button
                        leftIcon={<AddIcon />}
                        size="sm"
                        variant="outline"
                        colorScheme="orange"
                        onClick={() => {
                          const newId = Math.max(...roasThresholds.map(t => t.id)) + 1;
                          setRoasThresholds([...roasThresholds, { id: newId, month: '', minRoasPct: '' }]);
                        }}
                      >
                        Add Month
                      </Button>
                    )}
                  </Box>
                </Box>
                )}

                <RoasVectorEditor
                  points={roasVector}
                  onChange={setRoasVector}
                  isDisabled={isPageDisabled}
                />

                {fields.filter(f => ['cohortBreachesForFullRepayment', 'momDeltaPct', 'momDeltaUntilRoasPct', 'secondLevelBreachPct', 'pastCohortsToCheck', 'minRunrate', 'minCashBalance'].includes(f.name)).map((item, index) => {
                      return item.isHidden ? null : (
                          <Fragment key={index}>
                            <Flex align="center" gap={1}>
                              <Text>{item.label}</Text>
                              <FieldHelp item={item} />
                            </Flex>
                            <Box w={200}>
                              {item.isCustomField ? (
                                  item.element
                              ) : (
                                  <Controller
                                      control={control}
                                      name={item.name}
                                      render={renderInputElement({
                                        item,
                                        styleProps: {fontWeight: 800, w: 200},
                                        showPrefix: true,
                                        isDisabled: isPageDisabled,
                                        onChange: (name, value) => {
                                          onFieldChange(name, value);
                                        },
                                      })}
                                  />)}
                            </Box>
                          </Fragment>
                      );
                    }
                )}

                {/* Documents to Check */}
                <Box gridColumn="1 / -1" borderLeft="3px solid" borderColor="blue.500" pl={4} mt={4}>
                  <Flex align="center" gap={1} mb={3}>
                    <Text fontWeight="semibold" fontSize="sm" color="blue.600">
                      Documents to Check
                    </Text>
                    <Tooltip
                      label="Document names that need to be reviewed periodically (e.g. every few weeks)."
                      fontSize="xs"
                      maxW="300px"
                    >
                      <QuestionIcon boxSize={3} color="gray.500" cursor="help" />
                    </Tooltip>
                  </Flex>
                  <Box pb={4}>
                    {documentsToCheck.map((doc) => (
                      <Flex key={doc.id} gap={4} mb={3} align="center">
                        <Box w={320}>
                          <Input
                            value={doc.name}
                            placeholder="e.g., Bank Statement"
                            fontWeight={800}
                            onChange={(e) => {
                              const value = e.target.value;
                              setDocumentsToCheck((prev) =>
                                prev.map((item) =>
                                  item.id === doc.id ? { ...item, name: value } : item
                                )
                              );
                            }}
                            isDisabled={isPageDisabled}
                          />
                        </Box>
                        {!isPageDisabled && (
                          <IconButton
                            icon={<DeleteIcon />}
                            size="sm"
                            colorScheme="red"
                            variant="ghost"
                            onClick={() => {
                              setDocumentsToCheck((prev) => {
                                const next = prev.filter((item) => item.id !== doc.id);
                                return next.length > 0 ? next : [{ id: 1, name: '' }];
                              });
                            }}
                            isDisabled={documentsToCheck.length === 1 && !doc.name}
                            aria-label="Delete document"
                          />
                        )}
                      </Flex>
                    ))}
                    {!isPageDisabled && (
                      <Button
                        leftIcon={<AddIcon />}
                        size="sm"
                        variant="outline"
                        colorScheme="blue"
                        onClick={() => {
                          const newId = Math.max(...documentsToCheck.map((d) => d.id)) + 1;
                          setDocumentsToCheck([...documentsToCheck, { id: newId, name: '' }]);
                        }}
                      >
                        Add Document
                      </Button>
                    )}
                  </Box>
                </Box>
              </SimpleGrid>
              <Flex justify={'space-between'} mt={10}>
                {/* Hidden debug button - set to true to show */}
                {false && (
                <Button
                    variant={'outline'}
                    colorScheme={'gray'}
                    onClick={() => {
                      const debugData = {
                        formValues,
                        roasThresholds,
                        hasDuplicateMonths,
                        contractFile: contractFile ? { name: contractFile.name, size: contractFile.size } : null,
                      };
                      console.log('DEBUG - Current Form Data:', debugData);
                      toast({
                        title: 'Debug Data',
                        description: 'Form data logged to console. Open developer tools to view.',
                        status: 'info',
                        duration: 3000,
                        isClosable: true,
                      });
                    }}
                >
                  DEBUG
                </Button>
                )}
                <Button
                    type={'submit'}
                    variant={'brand'}
                    w={200}
                    ml={'auto'}
                    isDisabled={isPageDisabled || !isFormFilled() || hasDuplicateMonths || hasDuplicateRoasVectorMonths(roasVector)}
                >
                  Apply
                </Button>
              </Flex>
            </FormControl>
          </form>
        </Card>
      </Box>
  );
};

Deal.propTypes = {};

export default Deal;
